import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TaskService from "./TaskService";
import CenteredDialog from "./CenteredDialog";
import {
  employeeHasAccessToApplication,
  getApplicationMismatchMessages,
  getInvalidEmployeeFlagsForApplication,
} from "../utils/employeeApplicationAccess.js";
import {
  computeDueDateFromSlaWithLookups,
  formatAsDateTimeLocal,
} from "../utils/taskSlaDueDate.js";
import { refreshNotificationCount } from "../utils/notificationPolling";
import { getResponseTaskId } from "../utils/formatTaskSuccessMessage";
import "./CreateTaskScreen.css";

const APPLICATION_DETAILS_STORAGE_KEY = "application_details";

function persistApplicationDetailsToStorage(applicationDetails) {
  if (!Array.isArray(applicationDetails)) return;
  localStorage.setItem(
    APPLICATION_DETAILS_STORAGE_KEY,
    JSON.stringify(applicationDetails)
  );
}

function getEmptyCreateTaskFormData() {
  return {
    title: "",
    description: "",
    assignedTo: [],
    taskOwner: "",
    secondaryOwners: [],
    startDate: formatAsDateTimeLocal(new Date()) || "",
    dueDate: "",
    application: "",
    priority: "",
    taskType: "",
    status: "",
    compTypeId: "",
    compStatusTextDesc: "",
    releaseType: "",
    releaseDate: "",
  };
}

function resolveApplicationFunctionalLeadId(applicationId, applicationDetails) {
  const normalizedAppId = Number(applicationId);
  if (!Number.isFinite(normalizedAppId)) {
    return null;
  }
  let availableApplications = Array.isArray(applicationDetails)
    ? applicationDetails
    : [];
  if (!availableApplications.length) {
    try {
      availableApplications = JSON.parse(
        localStorage.getItem(APPLICATION_DETAILS_STORAGE_KEY) || "[]"
      );
    } catch (error) {
      availableApplications = [];
    }
  }
  const selectedApplication = availableApplications.find(
    (app) => Number(app?.aa_app_rl_application_id) === normalizedAppId
  );
  if (!selectedApplication) return null;
  const functionalLeadId = selectedApplication.aa_app_rl_application_functional_lead_id;
  return functionalLeadId == null ? null : Number(functionalLeadId);
}

const CreateTaskScreen = ({ userData = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = localStorage.getItem("employee_uid");
  const userRoleId = localStorage.getItem("user_role_id"); // Get role_id from localStorage
  const userApplicationId = localStorage.getItem("default_application_id"); // Get application_id from localStorage
  
  console.log(
    `The employee_uid value for create task screen page is ${currentUserId}`
  );
  console.log(`User role_id: ${userRoleId}, application_id: ${userApplicationId}`);

  // Create refs to track API calls - must be at component level
  const prioritiesApiComplete = useRef(false);
  const taskTypesApiComplete = useRef(false);
  const employeesApiComplete = useRef(false);
  const applicationsApiComplete = useRef(false);

  // Check if we have a pre-selected employee from navigation state
  const preSelectedEmployee = location.state?.preSelectedEmployee || null;
  console.log("Received preSelectedEmployee:", preSelectedEmployee);

  // Flag to determine if the assignedTo field should be disabled
  const isEmployeePreSelected = !!preSelectedEmployee;

  // State for loading and error handling
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, title: "", message: "", type: "success" });

  // State for data from API
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [applications, setApplications] = useState([]);
  const [completionTypes, setCompletionTypes] = useState([]); // Completion types from backend
  const [releaseTypes, setReleaseTypes] = useState([]); // From fetchAllLookupDetails.release_type_details
  const [taskSlaDetails, setTaskSlaDetails] = useState([]);

  // State for multi-select dropdown (Assign To)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Array of selected employee objects
  const [searchTerm, setSearchTerm] = useState(""); // For typeahead search
  const [filteredEmployees, setFilteredEmployees] = useState([]); // Filtered employee list

  // State for task owner dropdown (completely separate)
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const [filteredOwners, setFilteredOwners] = useState([]);

  // State for secondary owners dropdown (completely separate)
  const [isSecondaryOwnerDropdownOpen, setIsSecondaryOwnerDropdownOpen] = useState(false);
  const [selectedSecondaryOwners, setSelectedSecondaryOwners] = useState([]); // Array of selected secondary owner objects
  const [secondaryOwnerSearchTerm, setSecondaryOwnerSearchTerm] = useState("");
  const [filteredSecondaryOwners, setFilteredSecondaryOwners] = useState([]);

  // State for form data - assignedTo is now an array of employee UIDs
  const [formData, setFormData] = useState(() => getEmptyCreateTaskFormData());

  // State for file attachments
  const [attachedFiles, setAttachedFiles] = useState([]);
  const attachedFilesRef = useRef([]);
  // State for completion-specific file (single file when status is completed)
  const [completionFile, setCompletionFile] = useState(null);
  const completionFileRef = useRef(null);

  // Filter employees: optional application scope + search (Assign To)
  useEffect(() => {
    if (employees.length > 0) {
      const appId = formData.application;
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = searchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setFilteredEmployees(filtered);
    }
  }, [employees, searchTerm, formData.application]);

  // Filter owners: optional application scope + search
  useEffect(() => {
    if (employees.length > 0) {
      const appId = formData.application;
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = ownerSearchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setFilteredOwners(filtered);
    }
  }, [employees, ownerSearchTerm, formData.application]);

  // Filter secondary owners: optional application scope + search + exclude task owner
  useEffect(() => {
    if (employees.length > 0) {
      const taskOwnerId = formData.taskOwner ? String(formData.taskOwner).trim() : "";
      const appId = formData.application;
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const employeeId = String(employee.id || employee.uid || "").trim();
          if (taskOwnerId && employeeId && taskOwnerId === employeeId) {
            return false;
          }
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = secondaryOwnerSearchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setFilteredSecondaryOwners(filtered);
    }
  }, [employees, secondaryOwnerSearchTerm, formData.taskOwner, formData.application]);

  // Auto-select application if role_id is 3 and only one application is available
  useEffect(() => {
    if (userRoleId === "3" && userApplicationId && applications.length > 0) {
      // Handle comma-separated application IDs (e.g., "3,1")
      const appIds = userApplicationId.split(',').map(id => id.trim()).filter(id => id);
      const appIdNumbers = appIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      
      // Filter applications to only show those matching the user's application IDs
      const availableApps = applications.filter(app => 
        appIdNumbers.includes(app.aa_app_rl_application_id)
      );
      
      // If only one application is available, auto-select it
      if (availableApps.length === 1) {
        setFormData(prevData => ({
          ...prevData,
          application: String(availableApps[0].aa_app_rl_application_id)
        }));
      }
      // If multiple applications are available, don't auto-select (let user choose)
      // If no matching applications found, don't set anything
    }
  }, [applications, userRoleId, userApplicationId]);

  // Handle clicking outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the assign-to dropdown
      if (isDropdownOpen) {
        const assignToDropdown = event.target.closest('.assign-to-dropdown');
        if (!assignToDropdown) {
          setIsDropdownOpen(false);
        }
      }
      
      // Check if the click is outside the task-owner dropdown
      if (isOwnerDropdownOpen) {
        const ownerDropdown = event.target.closest('.owner-dropdown-container');
        if (!ownerDropdown) {
          setIsOwnerDropdownOpen(false);
        }
      }
      
      // Check if the click is outside the secondary-owner dropdown
      if (isSecondaryOwnerDropdownOpen) {
        const secondaryOwnerDropdown = event.target.closest('.secondary-owner-dropdown');
        if (!secondaryOwnerDropdown) {
          setIsSecondaryOwnerDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isOwnerDropdownOpen, isSecondaryOwnerDropdownOpen]);

  // Add keyboard support for accessibility and better UX
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isDropdownOpen || isOwnerDropdownOpen || isSecondaryOwnerDropdownOpen) {
        if (event.key === 'Escape') {
          setIsDropdownOpen(false);
          setIsOwnerDropdownOpen(false);
          setIsSecondaryOwnerDropdownOpen(false);
          setSearchTerm(""); // Clear search when closing
          setOwnerSearchTerm(""); // Clear owner search when closing
          setSecondaryOwnerSearchTerm(""); // Clear secondary owner search when closing
        }
      }
    };

    if (isDropdownOpen || isOwnerDropdownOpen || isSecondaryOwnerDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen, isOwnerDropdownOpen, isSecondaryOwnerDropdownOpen]);

  // Fetch all lookup details (priorities, task types, employees, applications) from API
  useEffect(() => {
    const fetchAllLookupData = async () => {
      if (!prioritiesApiComplete.current || !taskTypesApiComplete.current || !employeesApiComplete.current || !applicationsApiComplete.current) {
        try {
          setLoading(true);
          console.log("🚀 Fetching all lookup details...");
          
          const lookupData = await TaskService.fetchAllLookupDetails(true);
          console.log("✅ All lookup details received:", lookupData);

          // Set priorities
          if (lookupData.task_priority_details) {
            setPriorities(lookupData.task_priority_details);
            prioritiesApiComplete.current = true;
          }

          // Set task types
          if (lookupData.task_type_details) {
            setTaskTypes(lookupData.task_type_details);
            taskTypesApiComplete.current = true;
          }

          // Set statuses
          if (lookupData.task_status_details) {
            const sortedStatuses = lookupData.task_status_details.sort((a, b) => 
              a.aa_app_rl_task_status_name.localeCompare(b.aa_app_rl_task_status_name)
            );
            setStatuses(sortedStatuses);
            // Default to "Not started" (not first alphabetically — that was "Cancelled")
            setFormData((prevData) => {
              if (!prevData.status && sortedStatuses.length > 0) {
                const notStarted = sortedStatuses.find(
                  (s) =>
                    String(s.aa_app_rl_task_status_name || "")
                      .trim()
                      .toLowerCase() === "not started"
                );
                const defaultRow = notStarted || sortedStatuses[0];
                return {
                  ...prevData,
                  status: String(defaultRow.aa_app_rl_task_status_id),
                };
              }
              return prevData;
            });
          }

          // Process and set employees
          if (lookupData.employee_details) {
            const employeesData = lookupData.employee_details;
            console.log("Raw employee data:", employeesData);

            const formattedEmployees = employeesData.map((emp) => ({
              id: emp.aa_app_employee_uid,
              uid: emp.aa_app_employee_uid,
              employeeId: emp.aa_app_employee_id,
              name: emp.aa_app_employee_name,
              email: emp.aa_app_employee_email,
              department: emp.aa_app_resource_deptname,
              defaultApplicationIds: emp.aa_app_rl_default_application_id,
              roleId: emp.aa_app_rl_role_id,
            }));

            console.log("Formatted employees:", formattedEmployees);
            setEmployees(formattedEmployees);
            setFilteredEmployees(formattedEmployees); // Initialize filtered list
            setFilteredOwners(formattedEmployees); // Initialize filtered owners list

            // Set current employee as default task owner
            if (currentUserId) {
              const currentEmployee = formattedEmployees.find(
                (emp) => String(emp.id) === String(currentUserId)
              );
              
              if (currentEmployee) {
                console.log("Setting current employee as default owner:", currentEmployee);
                setSelectedOwner(currentEmployee);
                setOwnerSearchTerm(currentEmployee.name); // Show name in input
                setFormData((prevData) => ({
                  ...prevData,
                  taskOwner: String(currentEmployee.id),
                }));
              }
            }

            // Handle pre-selected employee
            if (preSelectedEmployee) {
              console.log("Looking for pre-selected employee:", preSelectedEmployee);

              let matchedEmployee;
              if (preSelectedEmployee.name) {
                matchedEmployee = employeesData.find(
                  (emp) =>
                    String(emp.aa_app_employee_id) === String(preSelectedEmployee.id) ||
                    String(emp.aa_app_employee_uid) === String(preSelectedEmployee.id)
                );
              } else {
                matchedEmployee = employeesData.find(
                  (emp) =>
                    String(emp.aa_app_employee_id) === String(preSelectedEmployee.id) ||
                    String(emp.aa_app_employee_uid) === String(preSelectedEmployee.id)
                );
              }

              if (matchedEmployee) {
                console.log("Found matching employee:", matchedEmployee);
                const selectedEmp = {
                  id: matchedEmployee.aa_app_employee_uid,
                  name: matchedEmployee.aa_app_employee_name,
                  email: matchedEmployee.aa_app_employee_email,
                  department: matchedEmployee.aa_app_resource_deptname,
                  defaultApplicationIds: matchedEmployee.aa_app_rl_default_application_id,
                  roleId: matchedEmployee.aa_app_rl_role_id,
                };

                setSelectedEmployees([selectedEmp]);
                setFormData((prevData) => ({
                  ...prevData,
                  assignedTo: [String(matchedEmployee.aa_app_employee_uid)],
                }));
              }
            }

            employeesApiComplete.current = true;
          }

          // Set applications
          if (lookupData.application_details) {
            setApplications(lookupData.application_details);
            persistApplicationDetailsToStorage(lookupData.application_details);
            applicationsApiComplete.current = true;
          }

          // Set completion types from backend
          if (lookupData.completion_type_details) {
            setCompletionTypes(lookupData.completion_type_details);
            console.log("✅ Completion types loaded from backend:", lookupData.completion_type_details);
          }

          if (lookupData.release_type_details) {
            setReleaseTypes(
              [...lookupData.release_type_details].sort(
                (a, b) => a.aa_app_rl_release_type_id - b.aa_app_rl_release_type_id
              )
            );
          }

          if (Array.isArray(lookupData.task_sla_details)) {
            setTaskSlaDetails(lookupData.task_sla_details);
          } else {
            setTaskSlaDetails([]);
          }

          // All data loaded, set loading to false
          if (prioritiesApiComplete.current && taskTypesApiComplete.current && employeesApiComplete.current && applicationsApiComplete.current) {
            setLoading(false);
            console.log("✅ All lookup data loaded successfully");
          }
        } catch (err) {
          console.error("Error fetching lookup details:", err);
          setError("Failed to load task options. Please try again.");
          setLoading(false);
          // Mark all as complete to prevent retry loops
          prioritiesApiComplete.current = true;
          taskTypesApiComplete.current = true;
          employeesApiComplete.current = true;
          applicationsApiComplete.current = true;
        }
      }
    };

    fetchAllLookupData();
  }, [preSelectedEmployee, currentUserId]);

  // Due date from SLA: start date/time + sla_duration_days when application, priority, and start are set
  useEffect(() => {
    const app = formData.application;
    const pri = formData.priority;
    const start = formData.startDate;
    if (!app || !pri || !start) {
      setFormData((prev) => (prev.dueDate === "" ? prev : { ...prev, dueDate: "" }));
      return;
    }
    if (!taskSlaDetails.length) {
      setFormData((prev) => (prev.dueDate === "" ? prev : { ...prev, dueDate: "" }));
      return;
    }
    const computed = computeDueDateFromSlaWithLookups(
      taskSlaDetails,
      app,
      pri,
      start,
      applications,
      priorities
    );
    setFormData((prev) => {
      if (!computed) {
        return prev.dueDate === "" ? prev : { ...prev, dueDate: "" };
      }
      return prev.dueDate === computed ? prev : { ...prev, dueDate: computed };
    });
  }, [
    formData.application,
    formData.priority,
    formData.startDate,
    taskSlaDetails,
    applications,
    priorities,
  ]);

  // Cleanup file preview URLs on unmount
  useEffect(() => {
    return () => {
      // Cleanup all preview URLs when component unmounts
      attachedFilesRef.current.forEach(fileObj => {
        if (fileObj.preview) {
          URL.revokeObjectURL(fileObj.preview);
        }
      });
      if (completionFileRef.current && completionFileRef.current.preview) {
        URL.revokeObjectURL(completionFileRef.current.preview);
      }
    };
  }, []); // Only run on unmount

  // Function to highlight search term in text
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <span key={index} className="search-highlight">{part}</span> : 
        part
    );
  };

  // Handle search input change for assigned to
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search input focus for assigned to
  const handleSearchFocus = () => {
    setIsDropdownOpen(true);
  };

  // Handle task owner search change
  const handleOwnerSearchChange = (e) => {
    const value = e.target.value;
    setOwnerSearchTerm(value);
    
    // If user clears the input, also clear the selected owner
    if (!value && selectedOwner) {
      setSelectedOwner(null);
      setFormData((prevData) => ({
        ...prevData,
        taskOwner: "",
      }));
    }
  };

  // Remove selected owner
  const removeSelectedOwner = () => {
    setSelectedOwner(null);
    setOwnerSearchTerm(""); // Clear the input text
    setFormData((prevData) => ({
      ...prevData,
      taskOwner: "",
    }));
  };

  // Handle task owner selection
  const handleOwnerSelect = (employee) => {
    setSelectedOwner(employee);
    setOwnerSearchTerm(employee.name); // Show selected name in input
    setFormData((prevData) => ({
      ...prevData,
      taskOwner: String(employee.id),
    }));
    setIsOwnerDropdownOpen(false);
  };

  // Clear search and close dropdown
  const clearSearch = () => {
    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  // Handle select all functionality
  const handleSelectAll = () => {
    if (isEmployeePreSelected) return;

    const allFilteredSelected = filteredEmployees.every(emp => 
      selectedEmployees.some(selected => selected.id === emp.id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered employees
      const remainingSelected = selectedEmployees.filter(selected => 
        !filteredEmployees.some(filtered => filtered.id === selected.id)
      );
      setSelectedEmployees(remainingSelected);
      setFormData((prevData) => ({
        ...prevData,
        assignedTo: remainingSelected.map((emp) => emp.id),
      }));
    } else {
      // Select all filtered employees
      const newSelections = filteredEmployees.filter(emp => 
        !selectedEmployees.some(selected => selected.id === emp.id)
      );
      const updatedSelected = [...selectedEmployees, ...newSelections];
      setSelectedEmployees(updatedSelected);
      setFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  // Handle employee selection/deselection
  const handleEmployeeToggle = (employee) => {
    if (isEmployeePreSelected) return; // Don't allow changes if pre-selected

    const isSelected = selectedEmployees.some((emp) => emp.id === employee.id);
    
    if (isSelected) {
      // Remove employee
      const updatedSelected = selectedEmployees.filter((emp) => emp.id !== employee.id);
      setSelectedEmployees(updatedSelected);
      setFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    } else {
      // Add employee
      const updatedSelected = [...selectedEmployees, employee];
      setSelectedEmployees(updatedSelected);
      setFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  // Remove a selected employee (from the tags)
  const removeSelectedEmployee = (employeeId) => {
    if (isEmployeePreSelected) return;

    const updatedSelected = selectedEmployees.filter((emp) => emp.id !== employeeId);
    setSelectedEmployees(updatedSelected);
    setFormData((prevData) => ({
      ...prevData,
      assignedTo: updatedSelected.map((emp) => emp.id),
    }));
  };

  // Handle secondary owner search input change
  const handleSecondaryOwnerSearchChange = (e) => {
    setSecondaryOwnerSearchTerm(e.target.value);
  };

  // Handle secondary owner search input focus
  const handleSecondaryOwnerSearchFocus = () => {
    setIsSecondaryOwnerDropdownOpen(true);
  };

  // Handle secondary owner selection/deselection
  const handleSecondaryOwnerToggle = (employee) => {
    // Prevent task owner from being selected as secondary owner
    const taskOwnerId = formData.taskOwner ? String(formData.taskOwner).trim() : '';
    const employeeId = String(employee.id || employee.uid || '').trim();
    if (taskOwnerId && employeeId && taskOwnerId === employeeId) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Task owner cannot be assigned as a secondary owner.",
        type: "error"
      });
      return;
    }
    
    const isSelected = selectedSecondaryOwners.some((emp) => emp.id === employee.id);
    
    if (isSelected) {
      // Remove secondary owner
      const updatedSelected = selectedSecondaryOwners.filter((emp) => emp.id !== employee.id);
      setSelectedSecondaryOwners(updatedSelected);
      setFormData((prevData) => ({
        ...prevData,
        secondaryOwners: updatedSelected.map((emp) => emp.id),
      }));
    } else {
      // Add secondary owner
      const updatedSelected = [...selectedSecondaryOwners, employee];
      setSelectedSecondaryOwners(updatedSelected);
      setFormData((prevData) => ({
        ...prevData,
        secondaryOwners: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  // Remove a selected secondary owner (from the tags)
  const removeSecondaryOwner = (employeeId) => {
    const updatedSelected = selectedSecondaryOwners.filter((emp) => emp.id !== employeeId);
    setSelectedSecondaryOwners(updatedSelected);
    setFormData((prevData) => ({
      ...prevData,
      secondaryOwners: updatedSelected.map((emp) => emp.id),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "dueDate" || name === "startDate") return;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "priority" || name === "application") {
        const app = next.application;
        const pri = next.priority;
        const start = next.startDate;
        if (!app || !pri || !start || !taskSlaDetails.length) {
          return { ...next, dueDate: "" };
        }
        const computed = computeDueDateFromSlaWithLookups(
          taskSlaDetails,
          app,
          pri,
          start,
          applications,
          priorities
        );
        return { ...next, dueDate: computed ?? "" };
      }
      return next;
    });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file: file,
      id: Date.now() + Math.random(), // Unique ID for each file
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setAttachedFiles(prevFiles => {
      const updated = [...prevFiles, ...newFiles];
      attachedFilesRef.current = updated;
      return updated;
    });
    
    // Reset the input
    e.target.value = '';
  };

  // Handle file removal
  const handleRemoveFile = (fileId) => {
    setAttachedFiles(prevFiles => {
      const fileToRemove = prevFiles.find(f => f.id === fileId);
      // Revoke object URL to free memory
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      const updated = prevFiles.filter(f => f.id !== fileId);
      attachedFilesRef.current = updated;
      return updated;
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle completion file selection (single file)
  const handleCompletionFileChange = (e) => {
    const file = e.target.files[0]; // Only take the first file
    if (file) {
      // Cleanup previous file preview if exists
      if (completionFileRef.current && completionFileRef.current.preview) {
        URL.revokeObjectURL(completionFileRef.current.preview);
      }
      
      const fileObj = {
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      };
      
      setCompletionFile(fileObj);
      completionFileRef.current = fileObj;
    }
    
    // Reset the input
    e.target.value = '';
  };

  // Handle completion file removal
  const handleRemoveCompletionFile = () => {
    if (completionFileRef.current && completionFileRef.current.preview) {
      URL.revokeObjectURL(completionFileRef.current.preview);
    }
    setCompletionFile(null);
    completionFileRef.current = null;
  };

  const resetCreateTaskForm = useCallback(() => {
    attachedFilesRef.current.forEach((fileObj) => {
      if (fileObj?.preview) {
        URL.revokeObjectURL(fileObj.preview);
      }
    });
    attachedFilesRef.current = [];
    setAttachedFiles([]);

    if (completionFileRef.current?.preview) {
      URL.revokeObjectURL(completionFileRef.current.preview);
    }
    completionFileRef.current = null;
    setCompletionFile(null);

    let defaultStatus = "";
    if (statuses.length > 0) {
      const notStarted = statuses.find(
        (s) =>
          String(s.aa_app_rl_task_status_name || "")
            .trim()
            .toLowerCase() === "not started"
      );
      const defaultRow = notStarted || statuses[0];
      defaultStatus = String(defaultRow.aa_app_rl_task_status_id);
    }

    let defaultTaskOwner = "";
    let defaultOwner = null;
    if (currentUserId && employees.length > 0) {
      const currentEmployee = employees.find((emp) => String(emp.id) === String(currentUserId));
      if (currentEmployee) {
        defaultOwner = currentEmployee;
        defaultTaskOwner = String(currentEmployee.id);
      }
    }

    setFormData({
      ...getEmptyCreateTaskFormData(),
      status: defaultStatus,
      taskOwner: defaultTaskOwner,
    });
    setSelectedEmployees([]);
    setSearchTerm("");
    setSelectedOwner(defaultOwner);
    setOwnerSearchTerm(defaultOwner?.name || "");
    setSelectedSecondaryOwners([]);
    setSecondaryOwnerSearchTerm("");
    setIsDropdownOpen(false);
    setIsOwnerDropdownOpen(false);
    setIsSecondaryOwnerDropdownOpen(false);
    setIsSubmitting(false);

    if (userRoleId === "3" && userApplicationId && applications.length > 0) {
      const appIds = userApplicationId.split(",").map((id) => id.trim()).filter(Boolean);
      const appIdNumbers = appIds.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id));
      const availableApps = applications.filter((app) =>
        appIdNumbers.includes(app.aa_app_rl_application_id)
      );
      if (availableApps.length === 1) {
        setFormData((prev) => ({
          ...prev,
          application: String(availableApps[0].aa_app_rl_application_id),
        }));
      }
    }
  }, [statuses, employees, currentUserId, userRoleId, userApplicationId, applications]);

  // No hardcoded mappings needed - completion type IDs are fetched from backend and used directly

  // Helper function to check if status is "completed"
  const isStatusCompleted = (statusId) => {
    if (!statusId || !statuses.length) return false;
    const status = statuses.find(s => s.aa_app_rl_task_status_id === parseInt(statusId));
    if (!status) return false;
    // Check if status name contains "completed" (case-insensitive)
    return status.aa_app_rl_task_status_name.toLowerCase().includes('completed');
  };

  const applicationEmployeeMismatch = useMemo(
    () => ({
      messages: getApplicationMismatchMessages(
        formData.application,
        selectedOwner,
        selectedSecondaryOwners,
        selectedEmployees
      ),
      ...getInvalidEmployeeFlagsForApplication(
        formData.application,
        selectedOwner,
        selectedSecondaryOwners,
        selectedEmployees
      ),
    }),
    [formData.application, selectedOwner, selectedSecondaryOwners, selectedEmployees]
  );

  const handleBack = () => {
    // Check if there are stored filters in localStorage
    const storedFilters = localStorage.getItem('taskFilters');
    
    if (storedFilters) {
      // Navigate back to all tasks with previous filters
      navigate('/all-tasks', { 
        state: { 
          filters: JSON.parse(storedFilters),
          preserveFilters: true 
        }
      });
    } else {
      // If no stored filters, just navigate to all tasks
      navigate('/all-tasks');
    }
  };

  /** Enter in single-line fields must not submit the form; use the Submit button. Textareas keep normal Enter for new lines. */
  const suppressEnterSubmit = useCallback((e) => {
    if (e.key !== "Enter") return;
    if (e.nativeEvent?.isComposing) return;
    if (e.target && String(e.target.tagName).toUpperCase() === "TEXTAREA") return;
    e.preventDefault();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

     if (isSubmitting) return;

     setIsSubmitting(true); // disable the button

    // Validate that at least one employee is selected
    // if (formData.assignedTo.length === 0) {
    //   alert("Please select at least one employee to assign the task to.");
    //   return;
    // }

    // Validate that a task owner is selected
    if (!formData.taskOwner) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a task owner.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    const mismatchMsgs = getApplicationMismatchMessages(
      formData.application,
      selectedOwner,
      selectedSecondaryOwners,
      selectedEmployees
    );
    if (mismatchMsgs.length > 0) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: mismatchMsgs.join(" "),
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate that a task type is selected
    if (!formData.taskType) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a task type.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate that a status is selected
    if (!formData.status) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a status.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    if (!formData.startDate) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please set the task start date and time.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    if (!formData.dueDate) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message:
          "Expected due date could not be calculated from SLA. Set start date/time, select application and priority, or verify SLA is configured for that pair.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate completion fields if status is "completed"
    if (isStatusCompleted(formData.status)) {
      console.log("Status is completed, checking compTypeId:", formData.compTypeId);
      if (!formData.compTypeId || formData.compTypeId === '' || formData.compTypeId === '0') {
        setDialog({
          isOpen: true,
          title: "Validation Error",
          message: "Please select a completion type. The completion type field is required when status is 'Completed'.",
          type: "error"
        });
        setIsSubmitting(false);
        return;
      }
      if (!formData.compStatusTextDesc || !formData.compStatusTextDesc.trim()) {
        setDialog({
          isOpen: true,
          title: "Validation Error",
          message: "Please enter completion notes. Completion notes are required when status is 'Completed'.",
          type: "error"
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      console.log("Form data at submission:", formData);
      console.log("Selected employees:", selectedEmployees);
      console.log("Selected owner:", selectedOwner);
      console.log("Selected secondary owners:", selectedSecondaryOwners);
      console.log("Attached files:", attachedFiles);

      /**
       * FORM DATA STRUCTURE SENT TO BACKEND
       * 
       * When NO files are attached:
       * - Content-Type: application/json
       * - Payload: JSON object (taskData)
       * 
       * When files ARE attached:
       * - Content-Type: multipart/form-data (set by browser)
       * - Payload: FormData object containing:
       *   - All taskData fields (arrays are appended multiple times with same key)
       *   - Files: 'attachments' key with File objects
       *   - File names: 'attachment_names' key with file names (optional)
       * 
       * API Endpoint: POST /createNewTask
       * 
       * JSON Structure (taskData):
       * {
       *   aa_app_rl_resource_task_uid: number (0 for new task),
       *   aa_app_rl_resource_task_title: string,
       *   aa_app_rl_resource_task_desc: string,
       *   aa_app_rl_application_id: number (parsed from formData.application),
       *   aa_app_task_assigned_to: number[] (array of employee UIDs),
       *   aa_app_task_owner_id: number (task owner UID),
       *   aa_app_task_secondary_owners: number[] (array of secondary owner UIDs, empty array if none),
       *   aa_app_rl_resource_task_priority_id: number (parsed from formData.priority),
       *   aa_app_rl_task_type_id: number (parsed from formData.taskType),
       *   aa_app_task_start_date: string (YYYY-MM-DDTHH:mm local datetime),
       *   aa_app_task_due_date: string (YYYY-MM-DDTHH:mm local datetime from form),
       *   aa_app_rl_task_status_id: number (default: 1),
       *   aa_app_rl_task_completion_percentage: number (default: 0),
       *   aa_app_rl_task_status_update_desc: null,
       *   aa_app_rl_task_status_issue_desc: null,
       *   aa_app_task_created_by: number (current user UID),
       *   aa_app_task_updated_by: number (current user UID)
       * }
       * 
       * FormData Structure (when files present):
       * - All above fields are appended as key-value pairs
       * - Arrays are appended multiple times: key=value1, key=value2, etc.
       * - attachments: File object (repeated for each file)
       * - attachment_names: string (file name, repeated for each file)
       */
      const taskData = {
        aa_app_rl_resource_task_uid: 0,
        aa_app_rl_resource_task_title: formData.title,
        aa_app_rl_resource_task_desc: formData.description,
        aa_app_rl_application_id: parseInt(formData.application),
        aa_app_rl_application_functional_lead_id: resolveApplicationFunctionalLeadId(
          formData.application,
          applications
        ),
        aa_app_task_assigned_to: formData.assignedTo.map(id => parseInt(id)), // Array of employee UIDs
        aa_app_task_owner_id: parseInt(formData.taskOwner), // Task owner UID
        aa_app_task_secondary_owners: formData.secondaryOwners && formData.secondaryOwners.length > 0 
          ? formData.secondaryOwners.map(id => parseInt(id)) 
          : [], // Array of secondary owner UIDs
        aa_app_rl_resource_task_priority_id: parseInt(formData.priority),
        aa_app_rl_task_type_id: parseInt(formData.taskType), // New field for task type
        aa_app_task_start_date: formData.startDate,
        aa_app_task_due_date: formData.dueDate,
        aa_app_rl_task_status_id: parseInt(formData.status),
        aa_app_rl_task_completion_percentage: 0,
        aa_app_rl_task_status_update_desc: null,
        aa_app_rl_task_status_issue_desc: null,
        aa_app_task_created_by: parseInt(currentUserId),
        aa_app_task_updated_by: parseInt(currentUserId)
      };

      if (formData.releaseType) {
        taskData.aa_app_rl_release_type_id = parseInt(formData.releaseType, 10);
      }
      if (formData.releaseDate) {
        taskData.aa_app_rl_release_date = formData.releaseDate;
      }

      // Always include completion fields when status is completed
      if (isStatusCompleted(formData.status)) {
        taskData.aa_app_rl_comp_type_id = parseInt(formData.compTypeId) || 0;
        taskData.aa_app_rl_comp_status_text_desc = formData.compStatusTextDesc || null;
        console.log("Adding completion fields to payload:", {
          comp_type_id: taskData.aa_app_rl_comp_type_id,
          comp_status_text_desc: taskData.aa_app_rl_comp_status_text_desc
        });
      }

      console.log("Submitting task data to backend:", JSON.stringify(taskData, null, 2));
      console.log("Completion file status:", {
        isCompleted: isStatusCompleted(formData.status),
        hasCompletionFile: completionFile !== null,
        completionFile: completionFile ? completionFile.name : null
      });

      // If files are attached, use FormData to send files along with task data
      let payload;
      let hasFiles = attachedFiles.length > 0;
      // Handle completion file if status is completed
      let hasCompletionFile = isStatusCompleted(formData.status) && completionFile !== null;
      
      // Log completion fields being sent
      if (isStatusCompleted(formData.status)) {
        console.log("Completion fields in payload:", {
          aa_app_rl_comp_type_id: taskData.aa_app_rl_comp_type_id,
          aa_app_rl_comp_status_text_desc: taskData.aa_app_rl_comp_status_text_desc,
          completion_file: hasCompletionFile ? completionFile.name : "No file"
        });
      }

      if (hasFiles || hasCompletionFile) {
        const formDataObj = new FormData();
        
        // Append task data as JSON string (API expects 'taskData' key)
        formDataObj.append('taskData', JSON.stringify(taskData));
        
        // Append all attached files
        if (hasFiles) {
          attachedFiles.forEach((fileObj, index) => {
            formDataObj.append('files[]', fileObj.file);
          });
        }
        
        // Append completion file (single file - API expects 'completion_file' or 'completionFile')
        if (hasCompletionFile) {
          formDataObj.append('completion_file', completionFile.file);
        }
        
        payload = formDataObj;
        console.log("Sending FormData with files");
        console.log("FormData entries:");
        // Log FormData contents (note: FormData is not directly loggable, so we show a summary)
        for (let pair of formDataObj.entries()) {
          if (pair[1] instanceof File) {
            console.log(`  ${pair[0]}: File(${pair[1].name}, ${pair[1].size} bytes, ${pair[1].type})`);
          } else {
            console.log(`  ${pair[0]}: ${pair[1]}`);
          }
        }
      } else {
        // No files - send as JSON
        payload = taskData;
        console.log("Sending JSON data (no files)");
      }

      // Call the TaskService to create the task with or without files
      const response = await TaskService.createTask(payload, hasFiles || hasCompletionFile);

      console.log("Task created successfully:", response);
      await refreshNotificationCount(undefined, { force: true });
      const createdTaskId = getResponseTaskId(response);
      const taskIdPart = createdTaskId != null ? ` (Task ID: ${createdTaskId})` : "";
      setDialog({
        isOpen: true,
        title: "Success",
        message: `Task created successfully${taskIdPart} and assigned to ${formData.assignedTo.length} employee(s)! Do you want to create another task?`,
        type: "success",
        isDualAction: true,
        primaryActionText: "Create another task",
        secondaryActionText: "Go to all tasks",
        onPrimaryAction: () => {
          setDialog((prev) => ({ ...prev, isOpen: false }));
          resetCreateTaskForm();
        },
        onSecondaryAction: () => {
          setDialog((prev) => ({ ...prev, isOpen: false }));
          handleBack();
        },
      });
    } catch (err) {
      console.error("Error creating task:", err);
      setDialog({
        isOpen: true,
        title: "Error",
        message: "Failed to create task. Please try again.",
        type: "error"
      });
      setIsSubmitting(false); 
    }
  };

  // Calculate select all state
  const allFilteredSelected = filteredEmployees.length > 0 && filteredEmployees.every(emp => 
    selectedEmployees.some(selected => selected.id === emp.id)
  );
  const someFilteredSelected = filteredEmployees.some(emp => 
    selectedEmployees.some(selected => selected.id === emp.id)
  );

  // Show loading state
  if (loading) {
    return (
      <div className="create-task-screen">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading form options...</p>
        </div>
      </div>
    );
  }

  // Show error message if there's an error
  if (error) {
    return (
      <div className="create-task-screen">
        <div className="error-message">
          <p>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-task-screen">
      <div className="screen-header">
        <h1 className="screen-title">Create New Task</h1>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back
        </button>
      </div>

      <div className="task-form-container">
        <form onSubmit={handleSubmit} onKeyDown={suppressEnterSubmit}>
          <div className="form-group">
            <label htmlFor="title">
              Task Title <span className="required-asterisk">*</span>
            </label>
            <input
              id="title"
              name="title"
              type="text"
              className="form-input"
              placeholder="Enter task title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="application">
              Application <span className="required-asterisk">*</span>
            </label>
            {(() => {
              // For role_id = 3, filter applications based on comma-separated default_application_id
              let availableApplications = applications;
              let isDisabled = false;
              let helpText = "";
              
              if (userRoleId === "3" && userApplicationId) {
                // Handle comma-separated application IDs (e.g., "3,1")
                const appIds = userApplicationId.split(',').map(id => id.trim()).filter(id => id);
                const appIdNumbers = appIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
                
                // Filter to show only applications matching the user's application IDs
                availableApplications = applications.filter(app => 
                  appIdNumbers.includes(app.aa_app_rl_application_id)
                );
                
                // Only disable if there's exactly 1 application (auto-selected)
                if (availableApplications.length === 1) {
                  isDisabled = true;
                  helpText = "Application is automatically selected based on your role";
                } else if (availableApplications.length > 1) {
                  isDisabled = false;
                  helpText = "Please select an application from your available applications";
                }
              }
              
              return (
                <>
                  <select
                    id="application"
                    name="application"
                    className="form-select"
                    value={formData.application}
                    onChange={handleInputChange}
                    required
                    disabled={isDisabled}
                  >
                    <option value="">Select an application</option>
                    {availableApplications
                      .slice()
                      .sort((a, b) =>
                        a.aa_app_rl_application_name.localeCompare(
                          b.aa_app_rl_application_name
                        )
                      )
                      .map((app) => (
                        <option
                          key={app.aa_app_rl_application_id}
                          value={app.aa_app_rl_application_id}
                        >
                          {app.aa_app_rl_application_name}
                        </option>
                      ))}
                  </select>
                  {userRoleId === "3" && helpText && (
                    <p className="help-text" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {helpText}
                    </p>
                  )}
                </>
              );
            })()}
            {applicationEmployeeMismatch.messages.length > 0 && (
              <div className="application-mismatch-banner" role="alert">
                {applicationEmployeeMismatch.messages.map((msg, idx) => (
                  <div key={idx}>{msg}</div>
                ))}
              </div>
            )}
          </div>

           <div className="form-group">
            <label htmlFor="description">
              Description <span className="required-asterisk">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              className="form-textarea"
              placeholder="Enter task description"
              value={formData.description}
              onChange={handleInputChange}
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="fileAttachment">
              Attachments (Images/Files/Screenshots)
            </label>
            <div className="file-attachment-container">
              <input
                id="fileAttachment"
                type="file"
                className="file-input"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                onChange={handleFileChange}
              />
              <label htmlFor="fileAttachment" className="file-input-label">
                <span className="file-input-icon">📎</span>
                <span className="file-input-text">Choose files to attach</span>
              </label>
              <p className="file-input-hint">
                Supported: Images, PDF, Word, Excel, Text files
              </p>
            </div>
            
            {/* Display attached files */}
            {attachedFiles.length > 0 && (
              <div className="attached-files-list">
                {attachedFiles.map((fileObj) => {
                  // Get file icon based on type or file extension - returns Font Awesome icon class
                  const getFileIcon = (type, fileName) => {
                    // First try based on MIME type - check Excel BEFORE Word to avoid false matches
                    if (type) {
                      if (type.includes('image')) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                      if (type.includes('pdf')) return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                      // Check Excel/spreadsheet BEFORE Word (Excel MIME types contain "document" which would match Word)
                      if (type.includes('excel') || type.includes('spreadsheet') || type.includes('application/vnd.ms-excel') || type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml') || type.includes('spreadsheetml')) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                      // Check PowerPoint before Word too
                      if (type.includes('powerpoint') || type.includes('presentation') || type.includes('application/vnd.ms-powerpoint') || type.includes('application/vnd.openxmlformats-officedocument.presentationml')) return <i className="fas fa-file-powerpoint" style={{ fontSize: '20px', color: '#FF9800' }}></i>;
                      // Now check Word (but exclude spreadsheet and presentation types)
                      if ((type.includes('word') || type.includes('msword') || type.includes('application/vnd.openxmlformats-officedocument.wordprocessingml')) && !type.includes('spreadsheet') && !type.includes('presentation')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                      // Generic document check (only if not spreadsheet or presentation)
                      if (type.includes('document') && !type.includes('spreadsheet') && !type.includes('presentation')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                      if (type.includes('video')) return <i className="fas fa-file-video" style={{ fontSize: '20px', color: '#9C27B0' }}></i>;
                      if (type.includes('audio')) return <i className="fas fa-file-audio" style={{ fontSize: '20px', color: '#FF5722' }}></i>;
                      if (type.includes('zip') || type.includes('rar') || type.includes('archive') || type.includes('compressed')) return <i className="fas fa-file-archive" style={{ fontSize: '20px', color: '#795548' }}></i>;
                      if (type.includes('csv') || type.includes('text/plain') || type.includes('text/csv')) return <i className="fas fa-file-csv" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                      if (type.includes('text')) return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                    }
                    
                    // Fallback to file extension
                    if (fileName) {
                      const extension = fileName.split('.').pop().toLowerCase();
                      // Images (including screenshots)
                      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'tif', 'heic', 'heif'].includes(extension)) {
                        return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                      }
                      // PDF
                      if (extension === 'pdf') {
                        return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                      }
                      // Word documents
                      if (['doc', 'docx', 'rtf', 'odt'].includes(extension)) {
                        return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                      }
                      // Excel/spreadsheets
                      if (['xls', 'xlsx', 'ods', 'xlsm'].includes(extension)) {
                        return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                      }
                      // PowerPoint/presentations
                      if (['ppt', 'pptx', 'odp'].includes(extension)) {
                        return <i className="fas fa-file-powerpoint" style={{ fontSize: '20px', color: '#FF9800' }}></i>;
                      }
                      // Text files
                      if (['txt', 'html', 'htm', 'css', 'js', 'json', 'xml'].includes(extension)) {
                        return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                      }
                      // CSV files
                      if (extension === 'csv') {
                        return <i className="fas fa-file-csv" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                      }
                      // Archives
                      if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension)) {
                        return <i className="fas fa-file-archive" style={{ fontSize: '20px', color: '#795548' }}></i>;
                      }
                      // Video
                      if (['mp4', 'avi', 'mov', 'wmv', 'webm', 'mkv', 'flv', 'm4v'].includes(extension)) {
                        return <i className="fas fa-file-video" style={{ fontSize: '20px', color: '#9C27B0' }}></i>;
                      }
                      // Audio
                      if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma'].includes(extension)) {
                        return <i className="fas fa-file-audio" style={{ fontSize: '20px', color: '#FF5722' }}></i>;
                      }
                    }
                    
                    // Default file icon
                    return <i className="fas fa-file" style={{ fontSize: '20px', color: '#757575' }}></i>;
                  };
                  
                  return (
                  <div key={fileObj.id} className="attached-file-item">
                    {fileObj.preview ? (
                      <div className="file-preview">
                        <img 
                          src={fileObj.preview} 
                          alt={fileObj.name}
                          className="file-preview-image"
                        />
                      </div>
                    ) : (
                      <div className="file-icon">
                        {getFileIcon(fileObj.type, fileObj.name)}
                      </div>
                    )}
                    <div className="file-info">
                      <div className="file-name">{fileObj.name}</div>
                      <div className="file-size">{formatFileSize(fileObj.size)}</div>
                    </div>
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={() => handleRemoveFile(fileObj.id)}
                      aria-label={`Remove ${fileObj.name}`}
                    >
                      ×
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">
                Priority <span className="required-asterisk">*</span>
              </label>
              <select
                id="priority"
                name="priority"
                className="form-select"
                value={formData.priority}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a priority</option>
                {priorities
                  .slice()
                  .sort(
                    (a, b) => b.aa_app_rl_priority_id - a.aa_app_rl_priority_id
                  )
                  .map((priority) => (
                    <option
                      key={priority.aa_app_rl_priority_id}
                      value={priority.aa_app_rl_priority_id}
                    >
                      {priority.aa_app_rl_priority_name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="taskType">
                Task Type <span className="required-asterisk">*</span>
              </label>
              <select
                id="taskType"
                name="taskType"
                className="form-select"
                value={formData.taskType}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a task type</option>
                {taskTypes
                  .slice()
                  .sort((a, b) => a.aa_app_rl_task_type_longname.localeCompare(b.aa_app_rl_task_type_longname))
                  .map((taskType) => (
                    <option
                      key={taskType.aa_app_rl_task_type_id}
                      value={taskType.aa_app_rl_task_type_id}
                    >
                      {taskType.aa_app_rl_task_type_longname}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Status field */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">
                Status <span className="required-asterisk">*</span>
              </label>
              <select
                id="status"
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a status</option>
                {statuses.map(status => (
                  <option 
                    key={status.aa_app_rl_task_status_id} 
                    value={status.aa_app_rl_task_status_id}
                  >
                    {status.aa_app_rl_task_status_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              {/* Empty div for spacing */}
            </div>
          </div>

          {/* Completion-specific fields (shown when status is "completed") */}
          {isStatusCompleted(formData.status) && (
            <div className="completion-fields-section" style={{ 
              marginTop: '20px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#4CAF50', fontSize: '18px' }}>
                Completion Details
              </h3>
              
              {/* Preset Dropdown */}
              <div className="form-group">
                <label htmlFor="compTypeId">
                  Completion Type <span className="required-asterisk">*</span>
                </label>
                <select 
                  id="compTypeId"
                  name="compTypeId"
                  className="form-select"
                  value={formData.compTypeId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select completion type</option>
                  {completionTypes.map(ct => (
                    <option key={ct.aa_app_rl_comp_type_id} value={String(ct.aa_app_rl_comp_type_id)}>
                      {ct.aa_app_rl_comp_type_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Text Field */}
              <div className="form-group">
                <label htmlFor="compStatusTextDesc">
                  Completion Notes <span className="required-asterisk">*</span>
                </label>
                <textarea 
                  id="compStatusTextDesc"
                  name="compStatusTextDesc"
                  className="form-textarea"
                  placeholder="Provide details about the completion..."
                  value={formData.compStatusTextDesc}
                  onChange={handleInputChange}
                  rows="4"
                  required
                ></textarea>
              </div>

              {/* Attachment Field - Single File */}
              <div className="form-group">
                <label htmlFor="completionFile">
                  Completion File (Images/Files/Screenshots)
                </label>
                <div className="file-attachment-container">
                  <input
                    id="completionFile"
                    type="file"
                    className="file-input"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={handleCompletionFileChange}
                  />
                  <label htmlFor="completionFile" className="file-input-label">
                    <span className="file-input-icon">📎</span>
                    <span className="file-input-text">Choose file to attach</span>
                  </label>
                  <p className="file-input-hint">
                    Supported: Images, PDF, Word, Excel, Text files
                  </p>
                </div>
                
                {/* Display attached completion file */}
                {completionFile && (
                  <div className="attached-files-list" style={{ marginTop: '15px' }}>
                    <div className="attached-file-item">
                      {completionFile.preview ? (
                        <div className="file-preview">
                          <img 
                            src={completionFile.preview} 
                            alt={completionFile.name}
                            className="file-preview-image"
                          />
                        </div>
                      ) : (
                        <div className="file-icon">
                          {(() => {
                            const getFileIcon = (type, fileName) => {
                              if (type) {
                                if (type.includes('image')) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                                if (type.includes('pdf')) return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                                if (type.includes('excel') || type.includes('spreadsheet') || type.includes('application/vnd.ms-excel') || type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml')) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                                if (type.includes('word') || type.includes('msword') || type.includes('application/vnd.openxmlformats-officedocument.wordprocessingml')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                                if (type.includes('text')) return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                              }
                              if (fileName) {
                                const extension = fileName.split('.').pop().toLowerCase();
                                if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                                if (extension === 'pdf') return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                                if (['doc', 'docx'].includes(extension)) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                                if (['xls', 'xlsx'].includes(extension)) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                              }
                              return <i className="fas fa-file" style={{ fontSize: '20px', color: '#757575' }}></i>;
                            };
                            return getFileIcon(completionFile.type, completionFile.name);
                          })()}
                        </div>
                      )}
                      <div className="file-info">
                        <div className="file-name">{completionFile.name}</div>
                        <div className="file-size">{formatFileSize(completionFile.size)}</div>
                      </div>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={handleRemoveCompletionFile}
                        aria-label={`Remove ${completionFile.name}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

         

          {/* Task Owner and Start Date in same row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskOwner">
                Task Owner <span className="required-asterisk">*</span>
              </label>
              
              {/* Task Owner Dropdown - Single Select with Search */}
              <div
                className={`owner-dropdown-container${applicationEmployeeMismatch.ownerInvalid ? " owner-dropdown-container--invalid" : ""}`}
                style={{ position: "relative" }}
              >
                <div className="search-container">
                  <input
                    type="text"
                    className="form-input search-input"
                    placeholder="Search and select task owner..."
                    value={ownerSearchTerm}
                    onChange={handleOwnerSearchChange}
                    onFocus={() => setIsOwnerDropdownOpen(true)}
                  />
                  {selectedOwner && (
                    <button
                      type="button"
                      className="clear-selection"
                      onClick={removeSelectedOwner}
                      style={{
                        position: 'absolute',
                        right: '30px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        color: '#666',
                        cursor: 'pointer',
                        padding: '2px'
                      }}
                      aria-label="Clear selection"
                    >
                      ×
                    </button>
                  )}
                  <button
                    type="button"
                    className="dropdown-trigger"
                    onClick={() => setIsOwnerDropdownOpen(!isOwnerDropdownOpen)}
                  >
                    <span className={`dropdown-arrow ${isOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>
                </div>

                {/* Owner Dropdown Menu */}
                {isOwnerDropdownOpen && (
                  <div className="dropdown-menu" style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    left: 0, 
                    right: 0, 
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {filteredOwners.length > 0 ? (
                      filteredOwners
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((employee) => {
                          const isSelected = selectedOwner && selectedOwner.id === employee.id;
                          return (
                            <div
                              key={employee.id}
                              className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleOwnerSelect(employee)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = isSelected ? '#e3f2fd' : 'transparent'}
                            >
                              <span>
                                {highlightSearchTerm(employee.name, ownerSearchTerm)}
                              </span>
                            </div>
                          );
                        })
                    ) : (
                      <div style={{ padding: '8px 12px', color: '#666' }}>
                        No employees found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group date-field start-date-field">
              <label htmlFor="startDate">
                Start date and time <span className="required-asterisk">*</span>
              </label>
              <input
                id="startDate"
                name="startDate"
                type="datetime-local"
                className="form-input"
                value={formData.startDate}
                readOnly
                step={60}
                title="Set when you open this form; not editable"
                required
              />
              <p className="help-text" style={{ marginTop: "6px" }}>
                Start is captured when you open this page and cannot be changed. Due date follows from this time plus SLA for the selected application and priority.
              </p>
            </div>
          </div>

          {/* Secondary Owners and Assign To in same row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="secondaryOwner">Secondary Owners</label>
              
              {/* Selected secondary owners display */}
              {selectedSecondaryOwners.length > 0 && (
                <div className="selected-employees">
                  {selectedSecondaryOwners.map((employee) => (
                    <span
                      key={employee.id}
                      className={`employee-tag${
                        applicationEmployeeMismatch.secondaryInvalidIds.has(employee.id)
                          ? " employee-tag--invalid"
                          : ""
                      }`}
                    >
                      {employee.name}
                      <button
                        type="button"
                        className="remove-employee"
                        onClick={() => removeSecondaryOwner(employee.id)}
                        aria-label={`Remove ${employee.name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Multi-select dropdown with search */}
              <div className="multi-select-dropdown secondary-owner-dropdown">
                {/* Search input with dropdown trigger */}
                <div className="search-container">
                  <input
                    type="text"
                    className="form-input search-input"
                    placeholder="Search employees..."
                    value={secondaryOwnerSearchTerm}
                    onChange={handleSecondaryOwnerSearchChange}
                    onFocus={handleSecondaryOwnerSearchFocus}
                  />
                  <button
                    type="button"
                    className="dropdown-trigger"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSecondaryOwnerDropdownOpen(!isSecondaryOwnerDropdownOpen);
                    }}
                  >
                    <span className={`dropdown-arrow ${isSecondaryOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>
                </div>

                {/* Dropdown options */}
                {isSecondaryOwnerDropdownOpen && (
                  <div className="dropdown-menu">
                    {/* Employee options */}
                    {filteredSecondaryOwners.length > 0 ? (
                      filteredSecondaryOwners
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((employee) => {
                          const isSelected = selectedSecondaryOwners.some(
                            (emp) => emp.id === employee.id
                          );
                          return (
                            <div
                              key={employee.id}
                              className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSecondaryOwnerToggle(employee);
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}} // Handled by parent onClick
                              />
                              <span className="option-text">
                                {highlightSearchTerm(employee.name, secondaryOwnerSearchTerm)}
                              </span>
                            </div>
                          );
                        })
                    ) : (
                      <div className="no-results">
                        No employees found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Hidden inputs for form submission */}
              {formData.secondaryOwners.map((employeeId, index) => (
                <input
                  key={index}
                  type="hidden"
                  name={`secondaryOwners[${index}]`}
                  value={employeeId}
                />
              ))}
            </div>

            <div className="form-group assign-to-field">
              <label htmlFor="assignedTo">Assign To</label>
              
              {/* Selected employees display */}
              {selectedEmployees.length > 0 && (
                <div className="selected-employees">
                  {selectedEmployees.map((employee) => (
                    <span
                      key={employee.id}
                      className={`employee-tag${
                        applicationEmployeeMismatch.assignedInvalidIds.has(employee.id)
                          ? " employee-tag--invalid"
                          : ""
                      }`}
                    >
                      {employee.name}
                      {!isEmployeePreSelected && (
                        <button
                          type="button"
                          className="remove-employee"
                          onClick={() => removeSelectedEmployee(employee.id)}
                          aria-label={`Remove ${employee.name}`}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* Multi-select dropdown with search */}
              {!isEmployeePreSelected ? (
                <div className="multi-select-dropdown assign-to-dropdown">
                  {/* Search input with dropdown trigger */}
                  <div className="search-container">
                    <input
                      type="text"
                      className="form-input search-input"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={handleSearchFocus}
                    />
                    <button
                      type="button"
                      className="dropdown-trigger"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDropdownOpen(!isDropdownOpen);
                      }}
                    >
                      <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
                    </button>
                  </div>

                  {/* Dropdown options */}
                  {isDropdownOpen && (
                    <div className="dropdown-menu">
                      {/* Employee options */}
                      {filteredEmployees.length > 0 ? (
                        filteredEmployees
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((employee) => {
                            const isSelected = selectedEmployees.some(
                              (emp) => emp.id === employee.id
                            );
                            return (
                              <div
                                key={employee.id}
                                className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEmployeeToggle(employee);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}} // Handled by parent onClick
                                />
                                <span className="option-text">
                                  {highlightSearchTerm(employee.name, searchTerm)}
                                </span>
                              </div>
                            );
                          })
                      ) : (
                        <div className="no-results">
                          No employees found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="pre-selected-info">
                  <p className="help-text">
                    Task will be assigned to the pre-selected employee
                  </p>
                </div>
              )}

              {/* Hidden inputs for form submission */}
              {formData.assignedTo.map((employeeId, index) => (
                <input
                  key={index}
                  type="hidden"
                  name={`assignedTo[${index}]`}
                  value={employeeId}
                />
              ))}
            </div>
          </div>
            
          {/* Date fields in a flex container */}
          <div className="form-row">

            <div className="form-group date-field due-date-field">
              <label htmlFor="dueDate">
                Expected Due Date <span className="required-asterisk">*</span>
              </label>
              <input
                id="dueDate"
                name="dueDate"
                type="datetime-local"
                className="form-input"
                value={formData.dueDate}
                readOnly
                step={60}
                title="Calculated from start date/time, application, priority, and SLA"
                required
              />
              <p className="help-text" style={{ marginTop: "6px" }}>
                Set from SLA: choose application and priority; due date and time update automatically from the start time above.
              </p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="releaseType">Release type</label>
              <select
                id="releaseType"
                name="releaseType"
                className="form-select"
                value={formData.releaseType}
                onChange={handleInputChange}
              >
                <option value="">Select release type</option>
                {(releaseTypes ?? []).map((rt) => (
                  <option
                    key={rt.aa_app_rl_release_type_id}
                    value={String(rt.aa_app_rl_release_type_id)}
                  >
                    {rt.aa_app_rl_release_type_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="releaseDate">Release date</label>
              <input
                id="releaseDate"
                name="releaseDate"
                type="date"
                className="form-input"
                value={formData.releaseDate}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleBack}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Task"}
            </button>
          </div>
        </form>
      </div>

      {/* Mandatory Fields Legend */}
      <div className="mandatory-legend">
        <span className="required-asterisk">*</span>
        <span className="legend-text">Indicates mandatory field</span>
      </div>

      {/* Loading Spinner Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-spinner-container">
            <div className="circular-spinner"></div>
            <p className="loading-text">Creating Task...</p>
          </div>
        </div>
      )}

      {/* Centered Dialog */}
      <CenteredDialog
        isOpen={dialog.isOpen}
        onClose={dialog.onClose || (() => setDialog({ ...dialog, isOpen: false }))}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        isConfirm={dialog.isConfirm || false}
        isDualAction={dialog.isDualAction || false}
        onConfirm={dialog.onConfirm || null}
        onPrimaryAction={dialog.onPrimaryAction || null}
        onSecondaryAction={dialog.onSecondaryAction || null}
        confirmText={dialog.confirmText || "Yes"}
        cancelText={dialog.cancelText || "No"}
        primaryActionText={dialog.primaryActionText || "OK"}
        secondaryActionText={dialog.secondaryActionText || "Cancel"}
      />
    </div>
  );
};

export default CreateTaskScreen;